/**
 * URL utility functions for sanitizing sensitive data
 */

/**
 * Removes various types of tokens from URLs and strings to prevent sensitive data leakage
 * @param urlString - The URL or string to sanitize
 * @returns The sanitized string with tokens replaced by '***'
 */
export function removeTokenFromUrl(urlString: string): string {
    if (!urlString) return urlString;

    // Define all token-related parameter names
    const tokenKeys = [
        'token', 'access_token', 'id_token', 'refresh_token',
        'auth_token', 'bearer_token', 'session_token', 'api_token',
        'oauth_token', 'jwt_token', 'csrf_token', 'xsrf_token'
    ];

    // Create regex patterns for different scenarios
    const createTokenRegexes = (keys: string[]) => ({
        // Query parameters: ?token=value or &token=value
        queryParams: new RegExp(`\\b(${keys.join('|')})=([A-Za-z0-9\\-_.%]+)`, 'gi'),

        // Hash fragments: #token=value or #access_token=value
        hashFragments: new RegExp(`#(${keys.join('|')})=([A-Za-z0-9\\-_.%]+)`, 'gi'),

        // JSON fields: "token":"value" or "access_token": "value"
        jsonFields: new RegExp(`"(${keys.join('|')})"\\s*:\\s*"[^"]*"`, 'gi'),

        // Path segments: /token/value or /access_token/value
        pathSegments: new RegExp(`\\/(${keys.join('|')})\\/[A-Za-z0-9\\-_.%]+`, 'gi'),

        // Generic token patterns (fallback)
        genericTokens: new RegExp(`\\b(${keys.join('|')})\\s*[:=]\\s*[A-Za-z0-9\\-_.%]+`, 'gi')
    });

    const tokenRegexes = createTokenRegexes(tokenKeys);

    try {
        // Try to parse as URL (handle both absolute and relative URLs)
        let url: URL;

        if (urlString.startsWith('http://') || urlString.startsWith('https://')) {
            // Absolute URL
            url = new URL(urlString);
        } else if (urlString.startsWith('/')) {
            // Relative URL - use dummy base for parsing
            url = new URL(urlString, 'http://dummy.com');
        } else {
            // Not a URL format, skip to regex-only approach
            throw new Error('Not a URL format');
        }

        // Handle query parameters
        tokenKeys.forEach(key => {
            if (url.searchParams.has(key)) {
                url.searchParams.set(key, '***');
            }
        });

        // Handle hash fragments (manually since URL API doesn't parse hash query params)
        if (url.hash) {
            url.hash = url.hash.replace(tokenRegexes.hashFragments, '#$1=***');
        }

        // Handle tokens in the URL path
        url.pathname = url.pathname.replace(tokenRegexes.pathSegments, '/$1/***');

        // Return the cleaned URL, removing dummy base if it was added
        const result = url.toString();
        return result.startsWith('http://dummy.com') ? result.replace('http://dummy.com', '') : result;

    } catch (e) {
    // Fallback to regex-based cleaning for non-URL strings or parsing failures
        let result = urlString;

        // Apply all regex patterns
        result = result.replace(tokenRegexes.queryParams, '$1=***');
        result = result.replace(tokenRegexes.hashFragments, '#$1=***');
        result = result.replace(tokenRegexes.jsonFields, '"$1":"***"');
        result = result.replace(tokenRegexes.pathSegments, '/$1/***');
        result = result.replace(tokenRegexes.genericTokens, '$1=***');

        // Handle percent-encoded tokens (decode, replace, encode back)
        try {
            const decoded = decodeURIComponent(result);
            if (decoded !== result) {
                // If decoding changed something, clean the decoded version and re-encode
                result = decoded
                    .replace(tokenRegexes.queryParams, '$1=***')
                    .replace(tokenRegexes.hashFragments, '#$1=***')
                    .replace(tokenRegexes.jsonFields, '"$1":"***"')
                    .replace(tokenRegexes.pathSegments, '/$1/***')
                    .replace(tokenRegexes.genericTokens, '$1=***');
            }
        } catch (decodeError) {
            // If decoding fails, continue with the original result
        }

        return result;
    }
}
